import { ButtonProtected } from '@components'

import { useModal, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ReconcilableResponse } from '@features/paymentReconciliationModal/query-api'
import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useMatchBankTransaction } from '@features/reconciliation/hooks'
import { invalidateReconciliationQueries } from '@features/reconciliation/utils'

import { queryClient } from '../../../../../../../../config/queryClient'
import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../../../contexts'
import { CurrencyMismatchModal as CurrencyMismatchModalComponent } from '../CurrencyMismatchModal'
import { getCurrencyMismatchData } from './utils/getCurrencyMismatchData'
import { getCurrencyMismatchModalId } from './utils/getCurrencyMismatchModalId'

const CurrencyMismatchModal = withModalConditionalRender(CurrencyMismatchModalComponent)

interface MatchButtonProps {
  onMatchSuccess?: () => void
}

export const MatchButton = ({ onMatchSuccess }: MatchButtonProps): ReactElement => {
  const { t } = useTranslation()
  const {
    hasBankReconciliationWriteScope,
    selectedTransaction,
    selectedBookkeepingItems,
    selectedBookkeepingItemIds,
    existingAssociations,
    combinedTransactions,
    hasDifference,
    differenceType,
    isCrossCurrency,
    setMatchError,
    clearAllItems,
    updateSelectedTransaction,
    currency,
    hasCreatedBookkeepingItemSelected,
  } = useBankTransactionDrawer()
  const sandbox = useSandboxModeContext()

  const modalId = getCurrencyMismatchModalId(selectedTransaction?.id)
  const { open: openCurrencyMismatchModal } = useModal(modalId)

  const handleMatchSuccess = useCallback(
    (data: ReconcilableResponse) => {
      clearAllItems()
      setMatchError(null)
      if (!sandbox) {
        invalidateReconciliationQueries(queryClient)
      }

      // Update selected transaction with the response data
      if (selectedTransaction && data.bankLines && data.bankLineMatches) {
        const matchedBankLine = data.bankLines.find((line) => line.id === selectedTransaction.id)
        const matchedBankLineMatch = data.bankLineMatches[0]

        if (matchedBankLine && matchedBankLineMatch) {
          updateSelectedTransaction({ ...matchedBankLine, match: matchedBankLineMatch })
        }
      }

      onMatchSuccess?.()
    },
    [clearAllItems, onMatchSuccess, sandbox, selectedTransaction, setMatchError, updateSelectedTransaction],
  )

  const { matchTransaction, isProcessing } = useMatchBankTransaction({
    onSuccess: handleMatchSuccess,
    onError: (errorMessage) => {
      setMatchError(errorMessage)
    },
    uiSource: UiSource.AccountingTransactionView,
    uiFlow: hasCreatedBookkeepingItemSelected ? BankMatchUiFlow.Created : BankMatchUiFlow.Matched,
  })

  const currencyMismatch = useMemo(
    () =>
      getCurrencyMismatchData({
        accountCurrency: currency,
        selectedBookkeepingItems,
        selectedTransaction,
        combinedTransactions: combinedTransactions ? Array.from(combinedTransactions.values()) : undefined,
      }),
    [selectedBookkeepingItems, selectedTransaction, currency, combinedTransactions],
  )

  const hasCurrencyMismatch = currencyMismatch !== null

  const handleMatch = useCallback(() => {
    setMatchError(null)

    if (!isCrossCurrency && hasDifference && !differenceType) {
      return
    }

    if (!selectedTransaction || selectedBookkeepingItems.length === 0) {
      return
    }

    if (sandbox) {
      handleMatchSuccess(sandbox.matchBankLineInSandbox(selectedTransaction.id, selectedBookkeepingItems))
      return
    }

    matchTransaction({
      selectedTransaction,
      selectedBookkeepingItems,
      differenceType: isCrossCurrency ? null : differenceType,
      accountCurrency: currency,
      existingAssociations,
      combinedTransactions,
    })
  }, [
    selectedTransaction,
    selectedBookkeepingItems,
    hasDifference,
    differenceType,
    isCrossCurrency,
    currency,
    existingAssociations,
    combinedTransactions,
    setMatchError,
    matchTransaction,
    sandbox,
    handleMatchSuccess,
  ])

  const handleButtonClick = useCallback(() => {
    if (!isCrossCurrency && hasDifference && !differenceType) {
      setMatchError(null)
      return
    }

    if (hasCurrencyMismatch) {
      openCurrencyMismatchModal()
      return
    }

    handleMatch()
  }, [
    isCrossCurrency,
    hasDifference,
    differenceType,
    hasCurrencyMismatch,
    openCurrencyMismatchModal,
    handleMatch,
    setMatchError,
  ])

  return (
    <>
      <ButtonProtected
        fullWidth
        disabled={hasBankReconciliationWriteScope && selectedBookkeepingItemIds.size === 0}
        loading={isProcessing}
        onClick={handleButtonClick}
        scopes={Scope.BankReconciliationWrite}
        trackingContext={TrackingContext.BankTransactions}
      >
        {t('bank_transactions.drawer.match_button')}
      </ButtonProtected>
      {currencyMismatch && <CurrencyMismatchModal id={modalId} data={currencyMismatch} onConfirm={handleMatch} />}
    </>
  )
}
